import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'

/**
 * 三端路由（附录C）：认证端 BlankLayout、个人中心 MeLayout、管理后台 AdminLayout。
 *
 * 页面按固定路径**全量静态注册**：菜单树（GET /api/me/v1/me/menus）只负责渲染侧边导航
 * 与越权兜底，不再是页面的唯一入口（否则菜单接口未就绪时任何页面都不可达）。
 * 菜单下发后仍会按 utils/route.ts 补充后端新增的页面，并按 path 去重。
 */
export const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/admin' },

  /* ---------------- 认证端 ---------------- */
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/Login.vue'),
    meta: { public: true, title: '登录' },
  },
  {
    path: '/mfa',
    name: 'mfa',
    component: () => import('@/views/auth/Mfa.vue'),
    meta: { public: true, title: '二次验证' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPassword.vue'),
    meta: { public: true, title: '找回密码' },
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/views/auth/Logout.vue'),
    meta: { public: true, title: '退出登录' },
  },
  {
    path: '/consent',
    name: 'consent',
    component: () => import('@/views/auth/Consent.vue'),
    meta: { public: true, title: '授权确认' },
  },
  {
    // 授权码回调（个人中心以应用身份接入，附录D）：中心跳回时带 code + state
    path: '/callback',
    name: 'oidc-callback',
    component: () => import('@/views/auth/Callback.vue'),
    meta: { public: true, title: '登录中' },
  },

  /* ---------------- 个人中心（附录C §4） ---------------- */
  {
    path: '/me',
    component: () => import('@/layouts/MeLayout.vue'),
    redirect: '/me/profile',
    children: [
      { path: 'profile', name: 'me-profile', component: () => import('@/views/me/Profile.vue'), meta: { title: '基本资料' } },
      { path: 'security', name: 'me-security', component: () => import('@/views/me/Security.vue'), meta: { title: '安全设置' } },
      { path: 'bindings', name: 'me-bindings', component: () => import('@/views/me/Bindings.vue'), meta: { title: '我的绑定' } },
      {
        path: 'authorizations',
        name: 'me-authorizations',
        component: () => import('@/views/me/Authorizations.vue'),
        meta: { title: '已授权应用' },
      },
      { path: 'sessions', name: 'me-sessions', component: () => import('@/views/me/Sessions.vue'), meta: { title: '登录设备' } },
      { path: 'logs', name: 'me-logs', component: () => import('@/views/me/Logs.vue'), meta: { title: '登录记录' } },
      { path: 'wechat', name: 'me-wechat', component: () => import('@/views/me/Wechat.vue'), meta: { title: '微信绑定' } },
      { path: 'privacy', name: 'me-privacy', component: () => import('@/views/me/Privacy.vue'), meta: { title: '隐私与数据' } },
    ],
  },

  /* ---------------- 管理后台（附录C §5） ---------------- */
  {
    path: '/admin',
    name: 'admin',
    component: () => import('@/layouts/AdminLayout.vue'),
    redirect: '/admin/dashboard',
    children: [
      { path: 'dashboard', name: 'admin-dashboard', component: () => import('@/views/admin/DashboardView.vue'), meta: { title: '首页' } },

      // 应用管理（§5.2）
      { path: 'apps', name: 'admin-apps', component: () => import('@/views/admin/AppsView.vue'), meta: { title: '应用管理' } },
      {
        path: 'apps/create',
        name: 'admin-apps-create',
        component: () => import('@/views/admin/AppCreateView.vue'),
        meta: { title: '创建应用' },
      },
      // 应用详情按 client_id 定位：自增主键不出现在 URL 里（可被枚举）
      { path: 'apps/:clientId', name: 'admin-apps-detail', component: () => import('@/views/admin/AppDetailView.vue'), meta: { title: '应用详情' } },
      {
        path: 'open-platforms',
        name: 'admin-open-platforms',
        component: () => import('@/views/admin/OpenPlatformsView.vue'),
        meta: { title: '微信开放平台' },
      },
      {
        path: 'open-platforms/:id',
        name: 'admin-open-platforms-detail',
        component: () => import('@/views/admin/OpenPlatformDetailView.vue'),
        meta: { title: '开放平台详情' },
      },

      // 用户管理（§5.3）
      { path: 'users', name: 'admin-users', component: () => import('@/views/admin/UsersView.vue'), meta: { title: '用户管理' } },
      { path: 'users/:id', name: 'admin-users-detail', component: () => import('@/views/admin/UserDetailView.vue'), meta: { title: '用户详情' } },

      // 绑定管理（§5.4）
      { path: 'bindings', name: 'admin-bindings', component: () => import('@/views/admin/BindingsView.vue'), meta: { title: '绑定管理' } },

      // 权限管理（§5.5）：不设独立的"权限点"页 —— 菜单自带授权码，按钮级能力随角色授权一并勾选
      {
        path: 'rbac/apps/:clientId/menus',
        name: 'admin-rbac-menus',
        component: () => import('@/views/admin/rbac/MenusView.vue'),
        meta: { title: '菜单管理' },
      },
      {
        path: 'rbac/apps/:clientId/roles',
        name: 'admin-rbac-roles',
        component: () => import('@/views/admin/rbac/RolesView.vue'),
        meta: { title: '角色' },
      },
      {
        path: 'rbac/roles/:roleId',
        name: 'admin-rbac-role-detail',
        component: () => import('@/views/admin/rbac/RoleDetailView.vue'),
        // 路径结构与菜单（rbac/apps/{clientId}/roles）不同，守卫据此判定归属菜单而非直接 403
        meta: { title: '角色详情', menuPath: 'rbac/apps/{clientId}/roles' },
      },
      {
        // 管理员授权：授权范围（全局 / 应用级）由表单选择，因此路径不绑定 appId
        path: 'rbac/admin-grants',
        name: 'admin-rbac-admin-grants',
        component: () => import('@/views/admin/rbac/AdminGrantsView.vue'),
        meta: { title: '管理员授权' },
      },

      // 冲突审核（§5.6）
      {
        path: 'conflicts',
        name: 'admin-conflicts',
        component: () => import('@/views/admin/conflicts/ConflictListView.vue'),
        meta: { title: '冲突审核工作台' },
      },
      // 固定子路径必须排在 `:id` 之前，否则会被当成工单详情
      {
        path: 'conflicts/rules',
        name: 'admin-conflicts-rules',
        component: () => import('@/views/admin/conflicts/MatchRulesView.vue'),
        meta: { title: '匹配规则配置' },
      },
      {
        path: 'conflicts/whitelist',
        name: 'admin-conflicts-whitelist',
        component: () => import('@/views/admin/conflicts/WhitelistView.vue'),
        meta: { title: '白名单' },
      },
      {
        path: 'conflicts/stats',
        name: 'admin-conflicts-stats',
        component: () => import('@/views/admin/conflicts/ConflictStatsView.vue'),
        meta: { title: '冲突趋势统计' },
      },
      {
        path: 'conflicts/:id',
        name: 'admin-conflicts-detail',
        component: () => import('@/views/admin/conflicts/ConflictDetailView.vue'),
        meta: { title: '冲突工单处理' },
      },

      // 审计与系统（§5.7）
      {
        path: 'system/audit-logs',
        name: 'admin-system-audit',
        component: () => import('@/views/admin/system/AuditLogsView.vue'),
        meta: { title: '审计日志' },
      },
      {
        path: 'system/login-logs',
        name: 'admin-system-login-logs',
        component: () => import('@/views/admin/system/LoginLogsView.vue'),
        meta: { title: '登录日志' },
      },
      {
        path: 'system/sessions',
        name: 'admin-system-sessions',
        component: () => import('@/views/admin/system/SessionsView.vue'),
        meta: { title: '会话管理' },
      },
      {
        path: 'system/config',
        name: 'admin-system-config',
        component: () => import('@/views/admin/system/SystemConfigView.vue'),
        meta: { title: '系统配置' },
      },
    ],
  },

  /* ---------------- 错误页 ---------------- */
  {
    path: '/403',
    name: 'forbidden',
    component: () => import('@/views/ForbiddenView.vue'),
    meta: { public: true, title: '无访问权限' },
  },
  {
    path: '/500',
    name: 'error',
    component: () => import('@/views/ErrorView.vue'),
    meta: { public: true, title: '服务异常' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { public: true, title: '页面不存在' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

export default router
